import React, { useMemo } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { truncDecimalNumber } from 'utilitys/helpers/general';

import { useThemeStatusStore } from 'store/themeStatus.store';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ProgressMeasurement } from 'components/common/GlobalFiltersInPage/components/progressMeasurementFilter/progressMeasurementFilter';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';

import { ActualTooltip } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/BaselineVsActual/components/ActualTooltip';
import { PlannedTooltip } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/BaselineVsActual/components/PlannedTooltip';
import { IBaselineVsActualResponse } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramProgressOverview/progress.type';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { CustomizedProgressBar, CustomizedTooltip } from 'components/common';

import classes from './BaselineVsActual.module.scss';

interface Props {
    data: IBaselineVsActualResponse;
    loading: boolean;
    progressWeighted?: ProgressMeasurement;
    milestoneFragnetTaskVersionHashCode?: string;
    displayTable?: boolean;
    displayTooltip?: boolean;
}

const BaselineVsActual = ({
    data,
    loading,
    progressWeighted,
    milestoneFragnetTaskVersionHashCode,
    displayTable = true,
}: Props) => {
    const {
        baseLine,
        actual,
        latestSubmittedAnticipatedDelay,
        criticalityPlannedPercent,
        criticalityActualPercent,
        criticalityScheduleAdherence,
        completionPlannedPercent,
        completionActualPercent,
        completionScheduleAdherence,
        resourceActualPercent,
        resourcePlannedPercent,
        resourceScheduleAdherence,
    } = data;

    const { actualPercent, plannedPercent, scheduleAdherence } = useMemo(() => {
        let actualPercent, plannedPercent, scheduleAdherence;
        if (data && progressWeighted) {
            switch (progressWeighted) {
                case 'activityCompleted':
                case 'activitySubmittedCompletion':
                    actualPercent = completionActualPercent;
                    plannedPercent = completionPlannedPercent;
                    scheduleAdherence = completionScheduleAdherence;
                    break;
                case 'activityCompletedCriticality':
                case 'activitySubmittedCompletionCriticality':
                    actualPercent = criticalityActualPercent;
                    plannedPercent = criticalityPlannedPercent;
                    scheduleAdherence = criticalityScheduleAdherence;
                    break;
                case 'resourceUnits':
                    actualPercent = resourceActualPercent;
                    plannedPercent = resourcePlannedPercent;
                    scheduleAdherence = resourceScheduleAdherence;
            }
        }
        return { actualPercent, plannedPercent, scheduleAdherence };
    }, [data, progressWeighted]);

    const baseLineValue = truncDecimalNumber(plannedPercent);
    const actualValue = truncDecimalNumber(actualPercent);
    const scheduleAdherenceValue = truncDecimalNumber(scheduleAdherence);

    const baseLineValueText = isNaN(baseLineValue) ? 'N/A' : `${baseLineValue}%`;
    const actualValueText = isNaN(actualValue) ? 'N/A' : `${actualValue}%`;

    const getScheduleAdherenceColorClass = (value) => {
        if (value < 0.9) {
            return 'red';
        } else if (value >= 0.9 && value < 1) {
            return 'amber';
        } else if (value >= 1) {
            return 'green';
        }
        return 'red';
    };

    const getScheduleAdherenceColor = getScheduleAdherenceColorClass(scheduleAdherenceValue);

    const { themeStatus } = useThemeStatusStore();
    const PlannedColor = themeStatus
        ? {
              backgroundImage: 'linear-gradient(90deg,#3340a2,#2d77b9 31%,#28abcf)',
          }
        : { backgroundColor: '#5A6DEE' };
    const ActualColor = themeStatus
        ? {
              backgroundImage: 'linear-gradient(90deg, rgb(2, 94, 91), rgb(1, 186, 184) 31%, rgb(0, 254, 254))',
          }
        : { backgroundColor: '#32A4BC' };

    const completionPlanLink = useMemo(() => {
        if (milestoneFragnetTaskVersionHashCode) {
            return AppRoutes.buildPathWithQuery(
                [AppRoutes.DASHBOARD_EXECUTION_PATH, executionConfig.completionPlan.link],
                {
                    completionPlanFragnetMilestones: milestoneFragnetTaskVersionHashCode,
                },
            );
        }

        return AppRoutes.buildPath(AppRoutes.DASHBOARD_EXECUTION_PATH, executionConfig.completionPlan.link);
    }, [milestoneFragnetTaskVersionHashCode]);

    return (
        <div className={classes.baselineVsActualContainer}>
            {loading && <OverlayWithSpinner />}
            <div className={classes.topSection}>
                <div data-testid={'left-side'} className={classes.leftSide}>
                    {baseLine && (
                        <CustomizedTooltip
                            tooltipContent={<PlannedTooltip baseLine={baseLine} />}
                            triggerElement={
                                <div data-testid={'base-line-bar'} className={classes.progressBarWrapper}>
                                    <span className={classes.progressBarTitle}>Planned</span>
                                    <CustomizedProgressBar
                                        currentValue={baseLineValue}
                                        progressBarStyles={PlannedColor}
                                        customTextClass={classes.baseLineText}
                                        text={baseLineValueText}
                                    />
                                </div>
                            }
                        />
                    )}
                    {actual && (
                        <CustomizedTooltip
                            tooltipContent={<ActualTooltip actual={actual} />}
                            triggerElement={
                                <div data-testid={'actual-line-bar'} className={classes.progressBarWrapper}>
                                    <span className={classes.progressBarTitle}>Actual</span>
                                    <CustomizedProgressBar
                                        currentValue={actualValue}
                                        customTextClass={classes.actualText}
                                        progressBarStyles={ActualColor}
                                        text={actualValueText}
                                    />
                                </div>
                            }
                        />
                    )}
                </div>

                <div data-testid={'right-side'} className={classes.rightSide}>
                    <div data-testid={'box'} className={`${classes.box} ${classes.current}`}>
                        <span data-testid={'title'} className={classes.title}>
                            Latest Submitted
                            <br /> Anticipated Delay
                        </span>
                        <span data-testid={'value'} className={`${classes.value} ${classes.capitalize}`}>
                            {latestSubmittedAnticipatedDelay}
                        </span>
                    </div>
                    <div data-testid={'box'} className={`${classes.box} ${classes.schedule}`}>
                        <span data-testid={'title'} className={classes.title}>
                            Schedule
                            <br /> Performance
                        </span>
                        <span
                            data-testid={'value'}
                            className={`${classes.value} ${classes[getScheduleAdherenceColor]}`}
                        >
                            {scheduleAdherenceValue}
                        </span>
                        <Link to={completionPlanLink} className={classes.link}>
                            Completion Plan
                        </Link>
                    </div>
                </div>
            </div>
            {displayTable && (
                <div data-testid={'table-selection'} className={classes.tableSection}>
                    <table>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Version Name</th>
                                <th>version date</th>
                                <th>start date</th>
                                <th>finish date</th>
                                <th>duration</th>
                                <th>total milestones (remaining)</th>
                                <th>total Tasks (remaining)</th>
                                <th>Max WBS level</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className={classes.spacer} />
                            <tr className={classes.spacer} />
                            <tr data-testid={'table-baseline'} className={classes.baseline}>
                                <td>Baseline (planned)</td>
                                <td>
                                    <CustomizedTooltip
                                        tooltipPosition={TooltipPosition.Top}
                                        tooltipContent={baseLine.versionName}
                                        triggerElement={<div className={classes.ellipsis}>{baseLine.versionName}</div>}
                                    />
                                </td>
                                <td className={classes.noWrap}>
                                    {moment(baseLine.versionDate).isValid()
                                        ? moment(baseLine.versionDate).format(constants.formats.date.default)
                                        : 'N/A'}
                                </td>
                                <td className={classes.noWrap}>
                                    {moment(baseLine.startDate).isValid()
                                        ? moment(baseLine.startDate).format(constants.formats.date.default)
                                        : 'N/A'}
                                </td>
                                <td className={classes.noWrap}>
                                    {moment(baseLine.finishDate).isValid()
                                        ? moment(baseLine.finishDate).format(constants.formats.date.default)
                                        : 'N/A'}
                                </td>
                                <td className={classes.noWrap}>{baseLine.duration} Months</td>
                                <td>
                                    {baseLine.totalMilestones.toLocaleString()} (
                                    {baseLine.totalMilestonesNotCompleted.toLocaleString()})
                                </td>
                                <td>
                                    {baseLine.totalTasks.toLocaleString()} (
                                    {baseLine.totalTasksNotCopmleted.toLocaleString()})
                                </td>
                                <td>{baseLine.maxWBSLevel}</td>
                            </tr>
                            <tr className={classes.spacer} />

                            <tr data-testid={'table-latest'} className={classes.latest}>
                                <td>Latest (actual)</td>
                                <td className={classes.noWrap}>
                                    <CustomizedTooltip
                                        tooltipPosition={TooltipPosition.Top}
                                        tooltipContent={actual.versionName}
                                        triggerElement={<div className={classes.ellipsis}>{actual.versionName}</div>}
                                    />
                                </td>
                                <td>
                                    {moment(actual.versionDate).isValid()
                                        ? moment(actual.versionDate).format(constants.formats.date.default)
                                        : 'N/A'}
                                </td>
                                <td>
                                    {moment(actual.startDate).isValid()
                                        ? moment(actual.startDate).format(constants.formats.date.default)
                                        : 'N/A'}
                                </td>
                                <td>
                                    {moment(actual.finishDate).isValid()
                                        ? moment(actual.finishDate).format(constants.formats.date.default)
                                        : 'N/A'}
                                </td>
                                <td className={classes.noWrap}>{actual.duration} Months</td>
                                <td>
                                    {actual.totalMilestones.toLocaleString()} (
                                    {actual.totalMilestonesNotCompleted.toLocaleString()})
                                </td>
                                <td>
                                    {actual.totalTasks.toLocaleString()} (
                                    {actual.totalTasksNotCopmleted.toLocaleString()})
                                </td>
                                <td>{actual.maxWBSLevel}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BaselineVsActual;
